import { Component, inject, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { CatanPinService } from '../../../core/services/catan-pin.service';

/**
 * Otključavanje osobnim PIN-om.
 *
 * PIN je i identitet: nakon provjere se zna tko je otključao, pa dijalog ponudi
 * odlazak na vlastiti profil.
 */
@Component({
  selector: 'app-catan-pin-dialog',
  standalone: true,
  imports: [FormsModule, DialogModule, InputTextModule, ButtonModule],
  template: `
    <p-dialog
      header="Tvoj PIN"
      [visible]="visible()"
      (visibleChange)="visible.set($event)"
      [modal]="true"
      [draggable]="false"
      [style]="{ width: '22rem' }"
      [breakpoints]="{ '640px': '92vw' }"
    >
      <p class="pin-hint">
        Upiši svojih 6 znamenki. Po njima te prepoznajemo — otključava unos rezultata i vodi na tvoj profil.
      </p>

      <div class="field">
        <label for="catanPin">PIN</label>
        <input
          pInputText
          id="catanPin"
          type="password"
          inputmode="numeric"
          autocomplete="off"
          maxlength="6"
          [(ngModel)]="pinValue"
          (keyup.enter)="submit()"
          [disabled]="loading()"
        />
      </div>

      @if (error()) {
        <small class="pin-error">{{ error() }}</small>
      }

      <p class="pin-hint pin-forgot">Zaboravio si PIN? Reci onome tko ima pristup da ti ga resetira.</p>

      <ng-template pTemplate="footer">
        <p-button label="Odustani" [text]="true" severity="secondary" (onClick)="visible.set(false)" />
        <p-button label="Otključaj" [loading]="loading()" (onClick)="submit()" />
      </ng-template>
    </p-dialog>
  `,
  styles: [
    `
      .pin-hint {
        margin: 0 0 1rem;
        color: var(--p-text-muted-color);
        font-size: 0.875rem;
      }

      .pin-forgot {
        margin: 1rem 0 0;
        font-size: 0.8rem;
      }

      .pin-error {
        display: block;
        margin-top: 0.5rem;
        color: var(--p-red-500);
      }
    `,
  ],
})
export class CatanPinDialogComponent {
  readonly visible = model<boolean>(false);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  pinValue = '';

  private pin = inject(CatanPinService);
  private messages = inject(MessageService);

  submit(): void {
    const value = this.pinValue.trim();

    if (!/^\d{6}$/.test(value)) {
      this.error.set('PIN ima točno 6 znamenki.');
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    this.pin.verify(value).subscribe({
      next: res => {
        this.loading.set(false);
        this.pinValue = '';
        this.visible.set(false);
        this.messages.add({
          severity: 'success',
          summary: `Bok, ${res.player.displayName}`,
          detail: 'Unos je otključan.',
          life: 3000,
        });
      },
      error: err => {
        this.loading.set(false);
        this.error.set(
          err.status === 429 ? 'Previše pokušaja. Pričekaj pa probaj ponovo.' : 'Taj PIN ne pripada nijednom igraču.',
        );
      },
    });
  }
}
